import type {
  KocDetail,
  KocListItem,
  KocPayload,
  JobListItem,
  LoginRequest,
  MeResponse,
  ProjectDetail,
  ProjectListItem,
  ProjectPayload,
  StatsKeywords,
  StatsOverview,
  VideoDetail,
  VideoImportPayload,
  VideoImportResponse,
  VideoListItem,
} from "@koc/shared";

const BASE = "/api";

let csrfToken: string | null = null;
export function setCsrfToken(t: string | null) {
  csrfToken = t;
}
export function getCsrfToken() {
  return csrfToken;
}

class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly body: unknown) {
    super(message);
  }
}

async function call<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const isWrite = method !== "GET" && method !== "HEAD";
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (isWrite && csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let parsed: unknown = null;
    try { parsed = await res.json(); } catch { /* ignore */ }
    throw new ApiError(`${method} ${path} failed: ${res.status}`, res.status, parsed);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ─── Auth ───────────────────────────────────────────────────
export const apiAuth = {
  me: () => call<MeResponse>("GET", "/auth/me"),
  login: (body: LoginRequest) => call<MeResponse>("POST", "/auth/login", body),
  logout: () => call<{ ok: true }>("POST", "/auth/logout"),
};

// ─── Projects ───────────────────────────────────────────────
export const apiProjects = {
  list: (search?: string) =>
    call<{ items: ProjectListItem[] }>("GET", `/projects${search ? `?search=${encodeURIComponent(search)}` : ""}`),
  get: (id: number) => call<ProjectDetail>("GET", `/projects/${id}`),
  create: (body: ProjectPayload) => call<{ id: number }>("POST", "/projects", body),
  update: (id: number, body: Partial<ProjectPayload>) => call<{ ok: true }>("PATCH", `/projects/${id}`, body),
  remove: (id: number) => call<{ ok: true }>("DELETE", `/projects/${id}`),
  scrapeNow: (id: number) => call<{ enqueued: number }>("POST", `/projects/${id}/scrape-now`),
};

// ─── KOCs ───────────────────────────────────────────────────
export const apiKocs = {
  list: (search?: string) =>
    call<{ items: KocListItem[] }>("GET", `/kocs${search ? `?search=${encodeURIComponent(search)}` : ""}`),
  get: (id: number) => call<KocDetail>("GET", `/kocs/${id}`),
  create: (body: KocPayload) => call<{ id: number }>("POST", "/kocs", body),
  update: (id: number, body: Partial<KocPayload>) => call<{ ok: true }>("PATCH", `/kocs/${id}`, body),
  remove: (id: number) => call<{ ok: true }>("DELETE", `/kocs/${id}`),
};

// ─── Videos ─────────────────────────────────────────────────
export type VideoFilters = {
  projectId?: number;
  kocId?: number;
  platform?: "tiktok" | "instagram" | "youtube";
  title?: string;
  keyword?: string;
  hasHit?: "1" | "0";
  publishedFrom?: number;
  publishedTo?: number;
  limit?: number;
  offset?: number;
};

function videoQuery(f: VideoFilters): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== "") p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const apiVideos = {
  list: (f: VideoFilters = {}) => call<{ items: VideoListItem[] }>("GET", `/videos${videoQuery(f)}`),
  get: (id: number) => call<VideoDetail>("GET", `/videos/${id}`),
  exportCsvUrl: (f: VideoFilters = {}) => `${BASE}/videos/export.csv${videoQuery(f)}`,
  import: (body: VideoImportPayload) => call<VideoImportResponse>("POST", "/videos", body),
};

// ─── Jobs ───────────────────────────────────────────────────
export const apiJobs = {
  list: (params: { status?: string; jobType?: string; projectId?: number } = {}) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) p.set(k, String(v));
    return call<{ items: JobListItem[] }>("GET", `/jobs${p.toString() ? `?${p}` : ""}`);
  },
  retry: (id: number) => call<{ ok: true }>("POST", `/jobs/${id}/retry`),
};

// ─── Stats ──────────────────────────────────────────────────
export const apiStats = {
  overview: () => call<StatsOverview>("GET", "/stats/overview"),
  keywords: () => call<StatsKeywords>("GET", "/stats/keywords"),
};
